import type { Paper } from './types';

const TITLE_TERM_WEIGHT = 8;
const TOPIC_TERM_WEIGHT = 6;
const TRACK_TERM_WEIGHT = 3;
const ABSTRACT_TERM_WEIGHT = 1;
const TITLE_PHRASE_BONUS = 12;
const TOPIC_PHRASE_BONUS = 8;
const ABSTRACT_PHRASE_BONUS = 2;
const EXACT_ID_SCORE = 20;
const MINIMUM_RELEVANCE_SCORE = 3;

function tokens(text: string): string[] {
  return (
    text
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLocaleLowerCase()
      .match(/[\p{L}\p{N}]+/gu) ?? []
  ).map((token) => {
    if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
    if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) {
      return token.slice(0, -1);
    }
    return token;
  });
}

function termCounts(values: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const token of values.flatMap(tokens)) {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  return counts;
}

function count(counts: ReadonlyMap<string, number>, term: string): number {
  return counts.get(term) ?? 0;
}

function normalizedPhrase(text: string): string {
  return tokens(text).join(' ');
}

function containsPhrase(values: readonly string[], phrase: string): boolean {
  return values.some((value) => normalizedPhrase(value).includes(phrase));
}

/**
 * Scores a paper for a review-panel text query.
 *
 * Every query term must occur in at least one searchable field. Title and topic
 * matches are intentionally much stronger than abstract mentions, and fewer
 * than three isolated abstract mentions do not pass the relevance threshold.
 */
export function scorePaperTextMatch(
  paper: Pick<Paper, 'id' | 'title' | 'abstract' | 'topics' | 'track'>,
  query: string,
): number {
  const queryTerms = [...new Set(tokens(query))];
  if (queryTerms.length === 0) return 0;

  const titleCounts = termCounts([paper.title]);
  const topicCounts = termCounts(paper.topics);
  const trackCounts = termCounts([paper.track]);
  const abstractCounts = termCounts([paper.abstract]);
  const idCounts = termCounts([paper.id]);

  let score = normalizedPhrase(paper.id) === normalizedPhrase(query) ? EXACT_ID_SCORE : 0;
  for (const term of queryTerms) {
    const titleMatches = count(titleCounts, term);
    const topicMatches = count(topicCounts, term);
    const trackMatches = count(trackCounts, term);
    const abstractMatches = count(abstractCounts, term);
    const idMatches = count(idCounts, term);
    if (
      titleMatches + topicMatches + trackMatches + abstractMatches + idMatches ===
      0
    ) {
      return 0;
    }
    score +=
      titleMatches * TITLE_TERM_WEIGHT +
      topicMatches * TOPIC_TERM_WEIGHT +
      trackMatches * TRACK_TERM_WEIGHT +
      Math.min(abstractMatches, 4) * ABSTRACT_TERM_WEIGHT;
  }

  if (queryTerms.length > 1) {
    const phrase = queryTerms.join(' ');
    if (containsPhrase([paper.title], phrase)) score += TITLE_PHRASE_BONUS;
    if (containsPhrase(paper.topics, phrase)) score += TOPIC_PHRASE_BONUS;
    if (containsPhrase([paper.abstract], phrase)) score += ABSTRACT_PHRASE_BONUS;
  }

  return score >= MINIMUM_RELEVANCE_SCORE ? score : 0;
}
